import React, { useMemo, useRef, useState } from "react";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { BellRing, Check, Loader2, Minus, Plus, RotateCcw } from "lucide-react";

import KitchenTicket, { TicketPrinter } from "./KitchenTicket";
import { DELIVERY_COST, SHOP, SLOT_TIMES, dish, upcomingDays, weekdayNow } from "./data";
import { AnimatedNumber, EASE, Em, MaskLines, Reveal, SectionTag, euro, scrollToTarget } from "./primitives";

const BASE_REVENUE = 1284;
const BASE_ORDERS = 37;
const BASE_PLANNED = 12;
const DEMO_DISHES = ["kroket", "burrata", "stoof", "steak", "mousse"].map(dish);

/* + knop die openklapt tot een teller */
function QtyControl({ qty, onAdd, onRemove, name }) {
   return (
      <motion.div
         layout
         transition={{ duration: 0.35, ease: EASE }}
         className={`flex h-10 items-center overflow-hidden rounded-full ${qty ? "bg-lp-ink text-lp-paper" : "bg-lp-ink/[0.06] text-lp-ink"}`}
      >
         <AnimatePresence initial={false} mode="popLayout">
            {qty > 0 && (
               <motion.button
                  key="min"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  onClick={onRemove}
                  aria-label={`${name} verwijderen`}
                  className="flex h-10 w-10 items-center justify-center"
               >
                  <Minus size={15} />
               </motion.button>
            )}
            {qty > 0 && (
               <motion.span
                  key={`q${qty}`}
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -12, opacity: 0 }}
                  className="w-4 text-center text-sm font-semibold tabular-nums"
               >
                  {qty}
               </motion.span>
            )}
         </AnimatePresence>
         <button
            onClick={onAdd}
            aria-label={`${name} toevoegen`}
            className="flex h-10 w-10 items-center justify-center transition-transform active:scale-90"
         >
            <Plus size={16} strokeWidth={2.4} />
         </button>
      </motion.div>
   );
}

/* Segmentknop met een bewegend pilletje */
function Segmented({ options, value, onChange, id }) {
   return (
      <div className="inline-flex rounded-full bg-lp-ink/[0.06] p-1">
         {options.map((o) => (
            <button
               key={o}
               onClick={() => onChange(o)}
               className={`relative rounded-full px-4 py-2 text-[13px] font-medium transition-colors ${
                  value === o ? "text-lp-paper" : "text-lp-ink/60 hover:text-lp-ink"
               }`}
            >
               {value === o && (
                  <motion.span layoutId={`seg-${id}`} className="absolute inset-0 rounded-full bg-lp-ink" transition={{ duration: 0.4, ease: EASE }} />
               )}
               <span className="relative">{o}</span>
            </button>
         ))}
      </div>
   );
}

export default function LiveDemo() {
   const [cart, setCart] = useState({ kroket: 1, stoof: 1 });
   const [mode, setMode] = useState("Afhalen");
   const days = useMemo(() => upcomingDays(4), []);
   const [dayIndex, setDayIndex] = useState(2);
   const [slot, setSlot] = useState("18:30");
   const day = days[dayIndex];
   const [status, setStatus] = useState("idle"); // idle · paying · paid
   const [orders, setOrders] = useState([]);
   const nextNumber = useRef(143);

   const lines = useMemo(
      () => DEMO_DISHES.filter((d) => cart[d.id]).map((d) => ({ name: d.name, qty: cart[d.id], price: d.price })),
      [cart]
   );
   const subtotal = lines.reduce((s, l) => s + l.qty * l.price, 0);
   const delivery = mode === "Levering" && subtotal > 0 ? DELIVERY_COST : 0;
   const total = subtotal + delivery;
   const count = lines.reduce((s, l) => s + l.qty, 0);

   const add = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
   const remove = (id) =>
      setCart((c) => {
         const n = { ...c, [id]: (c[id] || 0) - 1 };
         if (n[id] <= 0) delete n[id];
         return n;
      });

   const revenue = BASE_REVENUE + orders.reduce((s, o) => s + o.total, 0);
   const latest = orders[0];
   const earlier = orders.slice(1, 4);

   const pay = () => {
      if (!count || status !== "idle") return;
      setStatus("paying");
      setTimeout(() => {
         const now = new Date();
         const order = {
            number: String(nextNumber.current++).padStart(4, "0"),
            mode,
            slot: `${day.short} ${slot}`,
            when: `${day.long} om ${slot}`,
            time: `${weekdayNow()} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
            items: lines,
            deliveryCost: delivery,
            total,
         };
         setOrders((o) => [order, ...o]);
         setStatus("paid");
         if (window.innerWidth < 1024) setTimeout(() => scrollToTarget("#demo-keuken", -90), 500);
      }, 1100);
   };

   const again = () => {
      setCart({});
      setStatus("idle");
   };

   return (
      <section id="demo" className="lp-grain relative bg-lp-paper px-5 pb-28 pt-8 sm:px-8 sm:pb-40">
         <div className="mx-auto max-w-[1400px]">
            <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
               <div className="lg:col-span-7">
                  <SectionTag>Live demo</SectionTag>
                  <MaskLines
                     as="h2"
                     className="mt-8 text-[clamp(2.8rem,7vw,6.5rem)] font-semibold leading-[0.92] tracking-[-0.05em] text-lp-ink"
                     lines={["Speel even ", <Em className="text-lp-blue">klant.</Em>]}
                  />
               </div>
               <Reveal className="lg:col-span-5 lg:pb-3">
                  <p className="max-w-md text-lg leading-relaxed text-lp-muted">
                     Bestel bij {SHOP.short}, voor vandaag of later deze week, en kijk hoe je bestelling bij de
                     zaak binnenkomt. Het werkt zoals in Resto-Online, alleen de kroketten zijn niet echt.
                  </p>
               </Reveal>
            </div>

            <Reveal y={60} className="mt-14 grid overflow-hidden rounded-[32px] border border-lp-ink/10 bg-lp-card shadow-[0_60px_120px_-60px_rgba(18,17,16,0.35)] lg:grid-cols-[1.1fr_0.9fr]">
               {/* ============ GAST ============ */}
               <div className="relative min-w-0 p-5 sm:p-10">
                  <div className="flex items-center justify-between gap-3">
                     <span className="font-ticket text-[10px] uppercase tracking-[0.18em] text-lp-muted">Wat je gast ziet</span>
                     <span className="truncate rounded-full border border-lp-ink/10 px-3 py-1 font-ticket text-[11px] text-lp-ink/70">
                        resto-online.be/<span className="text-lp-ink">{SHOP.slug}</span>
                     </span>
                  </div>

                  <div className="mt-8 flex flex-wrap items-end justify-between gap-5">
                     <div className="flex items-center gap-4">
                        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-lp-ink font-editorial text-3xl italic text-lp-paper">{SHOP.initial}</span>
                        <div>
                           <p className="text-2xl font-semibold tracking-[-0.03em] text-lp-ink">{SHOP.name}</p>
                           <p className="text-sm text-lp-muted">{SHOP.tagline}</p>
                        </div>
                     </div>
                     <LayoutGroup id="mode">
                        <Segmented id="mode" options={["Afhalen", "Levering"]} value={mode} onChange={setMode} />
                     </LayoutGroup>
                  </div>

                  {/* menukaart */}
                  <ul className="mt-8 divide-y divide-lp-ink/[0.08] border-y border-lp-ink/[0.08]">
                     {DEMO_DISHES.map((d) => (
                        <li key={d.id} className="flex items-center gap-4 py-3.5">
                           <img src={d.thumb} alt="" loading="lazy" width={56} height={56} className="!h-12 !w-12 flex-shrink-0 rounded-xl object-cover sm:!h-14 sm:!w-14" />
                           <div className="min-w-0 flex-1">
                              <div className="flex items-baseline text-[16px] font-medium text-lp-ink sm:text-[17px]">
                                 <span className="min-w-0 leading-snug sm:truncate">{d.name}</span>
                                 <span className="lp-leader hidden sm:block" />
                                 <span className="hidden tabular-nums sm:inline">{euro(d.price)}</span>
                              </div>
                              <p className="mt-0.5 truncate text-[13px] text-lp-muted">{d.desc}</p>
                              <p className="mt-0.5 text-[14px] font-medium tabular-nums text-lp-ink sm:hidden">{euro(d.price)}</p>
                           </div>
                           <QtyControl qty={cart[d.id] || 0} name={d.name} onAdd={() => add(d.id)} onRemove={() => remove(d.id)} />
                        </li>
                     ))}
                  </ul>

                  {/* moment */}
                  <div className="mt-7">
                     <div className="flex items-baseline justify-between">
                        <p className="text-sm font-medium text-lp-ink">{mode === "Afhalen" ? "Afhaalmoment" : "Levermoment"}</p>
                        <AnimatePresence mode="wait">
                           <motion.p
                              key={mode}
                              initial={{ opacity: 0, y: 6 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -6 }}
                              className="text-[12px] text-lp-muted"
                           >
                              {mode === "Afhalen" ? SHOP.address : `Postcode ${SHOP.postcode} · levering ${euro(DELIVERY_COST)}`}
                           </motion.p>
                        </AnimatePresence>
                     </div>
                     <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {days.map((d) => (
                           <button
                              key={d.i}
                              onClick={() => setDayIndex(d.i)}
                              className={`relative rounded-xl px-2 py-2 text-left transition-colors ${
                                 dayIndex === d.i ? "text-lp-paper" : "bg-lp-ink/[0.05] text-lp-ink hover:bg-lp-ink/10"
                              }`}
                           >
                              {dayIndex === d.i && (
                                 <motion.span layoutId="day" className="absolute inset-0 rounded-xl bg-lp-ink" transition={{ duration: 0.4, ease: EASE }} />
                              )}
                              <span className="relative block truncate text-[13px] font-medium">{d.label}</span>
                              <span className={`relative block text-[11px] ${dayIndex === d.i ? "text-lp-paper/60" : "text-lp-muted"}`}>
                                 {d.date}
                              </span>
                           </button>
                        ))}
                     </div>
                     <div className="mt-2 flex flex-wrap gap-2">
                        {SLOT_TIMES.map((t) => (
                           <button
                              key={t}
                              onClick={() => setSlot(t)}
                              className={`relative rounded-xl px-3.5 py-2 text-[13px] font-medium tabular-nums transition-colors ${
                                 slot === t ? "text-lp-paper" : "bg-lp-ink/[0.05] text-lp-ink hover:bg-lp-ink/10"
                              }`}
                           >
                              {slot === t && (
                                 <motion.span layoutId="slot" className="absolute inset-0 rounded-xl bg-lp-ink" transition={{ duration: 0.4, ease: EASE }} />
                              )}
                              <span className="relative">{t}</span>
                           </button>
                        ))}
                     </div>
                  </div>

                  {/* totaal + afrekenen */}
                  <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                     <div>
                        <p className="text-[12px] text-lp-muted">
                           {count} {count === 1 ? "gerecht" : "gerechten"}
                           {delivery ? ` · incl. ${euro(delivery)} levering` : ""}
                        </p>
                        <p className="text-3xl font-semibold tracking-[-0.03em] text-lp-ink">
                           <AnimatedNumber value={total} format={euro} />
                        </p>
                     </div>
                     <button
                        onClick={pay}
                        disabled={!count || status !== "idle"}
                        className="group relative flex h-14 items-center justify-center gap-2 overflow-hidden rounded-full bg-lp-blue px-7 text-[15px] font-medium text-white transition-[opacity,transform] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
                     >
                        <span aria-hidden className="absolute inset-0 origin-bottom scale-y-0 rounded-full bg-lp-ink transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-y-100 group-disabled:!scale-y-0" />
                        <span className="relative flex items-center gap-2">
                           {status === "paying" ? (
                              <>
                                 <Loader2 size={17} className="animate-spin" /> Betalen met Bancontact…
                              </>
                           ) : (
                              <>Bestellen & betalen</>
                           )}
                        </span>
                     </button>
                  </div>

                  {/* bedankt */}
                  <AnimatePresence>
                     {status === "paid" && latest && (
                        <motion.div
                           className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-lp-blue p-8 text-center text-white"
                           initial={{ clipPath: "circle(0% at 85% 92%)" }}
                           animate={{ clipPath: "circle(150% at 85% 92%)" }}
                           exit={{ clipPath: "circle(0% at 85% 92%)" }}
                           transition={{ duration: 0.9, ease: EASE }}
                        >
                           <motion.span
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 0.35, type: "spring", stiffness: 300, damping: 16 }}
                              className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-lp-blue"
                           >
                              <Check size={30} strokeWidth={3} />
                           </motion.span>
                           <MaskLines
                              play
                              delay={0.4}
                              className="mt-7 text-[clamp(2.4rem,5vw,3.6rem)] leading-none"
                              lines={[<Em>Dank je wel!</Em>]}
                           />
                           <p className="mt-4 max-w-xs text-white/80">
                              Bestelling #{latest.number} is betaald. {latest.mode === "Afhalen" ? "Afhalen" : "Levering"} {latest.when}.
                              Je krijgt een bevestiging per mail.
                           </p>
                           <button
                              onClick={again}
                              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium text-lp-blue transition-transform hover:scale-[1.03] active:scale-[0.98]"
                           >
                              <RotateCcw size={15} /> Nog een bestelling
                           </button>
                        </motion.div>
                     )}
                  </AnimatePresence>
               </div>

               {/* ============ KEUKEN ============ */}
               <div id="demo-keuken" className="lp-grain lp-grain-dark relative min-w-0 bg-lp-ink p-5 text-lp-paper sm:p-10">
                  <div className="flex items-center justify-between">
                     <span className="font-ticket text-[10px] uppercase tracking-[0.18em] text-lp-paper/50">Wat jij ziet</span>
                     <span className="flex items-center gap-2 font-ticket text-[10px] uppercase tracking-[0.18em] text-emerald-400">
                        <span className="h-1.5 w-1.5 animate-blink rounded-full bg-emerald-400" /> Live
                     </span>
                  </div>

                  <div className="mt-8 grid grid-cols-[1.5fr_1fr_1fr] gap-px overflow-hidden rounded-2xl bg-white/10">
                     {[
                        { l: "Omzet deze week", v: <AnimatedNumber value={revenue} format={euro} /> },
                        { l: "Bestellingen", v: <AnimatedNumber value={BASE_ORDERS + orders.length} /> },
                        { l: "Gepland", v: <AnimatedNumber value={BASE_PLANNED + orders.length} /> },
                     ].map((s) => (
                        <div key={s.l} className="bg-lp-ink p-3 sm:p-4">
                           <p className="truncate text-[11px] text-lp-paper/50">{s.l}</p>
                           <p className="mt-1 whitespace-nowrap text-[15px] font-semibold tracking-[-0.02em] sm:text-lg xl:text-xl">{s.v}</p>
                        </div>
                     ))}
                  </div>

                  {/* melding */}
                  <div className="relative mt-5 h-14">
                     <AnimatePresence mode="wait">
                        {latest ? (
                           <motion.div
                              key={latest.number}
                              initial={{ opacity: 0, y: -14, scale: 0.96 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              exit={{ opacity: 0, y: 10 }}
                              transition={{ duration: 0.5, ease: EASE, delay: 0.15 }}
                              className="flex items-center gap-3 rounded-2xl bg-white/[0.07] px-4 py-3 ring-1 ring-white/10"
                           >
                              <motion.span
                                 animate={{ rotate: [0, -18, 16, -10, 6, 0] }}
                                 transition={{ duration: 0.8, delay: 0.5 }}
                                 className="flex h-8 w-8 items-center justify-center rounded-lg bg-lp-blue"
                              >
                                 <BellRing size={15} />
                              </motion.span>
                              <span className="text-sm">
                                 <span className="font-semibold">Nieuwe bestelling #{latest.number}</span>
                                 <span className="text-lp-paper/50"> · {latest.slot}</span>
                              </span>
                           </motion.div>
                        ) : (
                           <motion.p key="wait" exit={{ opacity: 0 }} className="flex h-full items-center gap-2 text-sm text-lp-paper/45">
                              Wachten op je bestelling
                              <span className="flex gap-1">
                                 {[0, 1, 2].map((i) => (
                                    <motion.span
                                       key={i}
                                       className="h-1 w-1 rounded-full bg-lp-paper/50"
                                       animate={{ opacity: [0.2, 1, 0.2] }}
                                       transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                                    />
                                 ))}
                              </span>
                           </motion.p>
                        )}
                     </AnimatePresence>
                  </div>

                  {/* de bestelling zelf */}
                  <div className="mx-auto mt-4 max-w-[330px]">
                     <TicketPrinter printing={!!latest} ticketKey={latest?.number || "leeg"} label="Nieuwe bestellingen">
                        {latest ? (
                           <KitchenTicket order={latest} stamp stampDelay={1.5} />
                        ) : (
                           <div className="lp-ticket-bottom h-40" />
                        )}
                     </TicketPrinter>
                  </div>

                  <p className="-mt-2 text-center font-ticket text-[10px] uppercase tracking-[0.16em] text-lp-paper/40">
                     Melding op je gsm · alles in je dashboard · bonprinter binnenkort
                  </p>

                  {/* eerdere bestellingen */}
                  {earlier.length > 0 && (
                     <ul className="mt-4 space-y-2">
                        <AnimatePresence initial={false}>
                           {earlier.map((o) => (
                              <motion.li
                                 key={o.number}
                                 layout
                                 initial={{ opacity: 0, height: 0 }}
                                 animate={{ opacity: 1, height: "auto" }}
                                 exit={{ opacity: 0, height: 0 }}
                                 className="flex items-center justify-between rounded-xl bg-white/[0.04] px-4 py-2.5 font-ticket text-[11px] text-lp-paper/60"
                              >
                                 <span>#{o.number}</span>
                                 <span>
                                    {o.mode} {o.slot}
                                 </span>
                                 <span className="text-lp-paper">{euro(o.total)}</span>
                              </motion.li>
                           ))}
                        </AnimatePresence>
                     </ul>
                  )}
               </div>
            </Reveal>
         </div>
      </section>
   );
}
